import * as modifierTypes from './constants';

/**
 * Sold-out / offline options from PosHub sync include ``is_active: false`` but remain in the payload
 * so staff can audit; ordering UIs must hide them. Missing ``is_active`` counts as active.
 */
export function getActiveModifierOptions(modifier) {
  if (!modifier?.options?.length) {
    return [];
  }
  const active = modifier.options.filter((o) => o.is_active !== false);
  return [...active].sort((a, b) => {
    const pa = a.position ?? 0;
    const pb = b.position ?? 0;
    if (pa !== pb) return pa - pb;
    return (a.id ?? 0) - (b.id ?? 0);
  });
}

/** PosHub API may return `modifiers_children` as full nested modifier group objects or numeric ids. */
export function optionLinksChildModifierGroup(parentOption, childModifierGroupId) {
  if (!parentOption?.modifiers_children?.length || childModifierGroupId == null) return false;
  return parentOption.modifiers_children.some((c) => {
    const id = typeof c === 'object' && c !== null ? c.id : c;
    return id === childModifierGroupId;
  });
}

export function getModifierMinSelections(modifier) {
  return Math.min(
    getModifierMaxSelections(modifier),
    Math.max(modifier.min_selections || 0, modifier.mandatory ? 1 : 0),
  );
}

export function getModifierMaxSelections(modifier) {
  if (modifier.modifier_type === modifierTypes.Single_Selected) {
    return 1;
  }
  const activeLen = getActiveModifierOptions(modifier).length;
  return modifier.max_selections ?? activeLen;
}

export function hasOptionChildren(option) {
  return option.modifiers_children?.length > 0;
}

export function getModifierSelectionsText(modifier) {
  const maxSelections = getModifierMaxSelections(modifier);
  const minSelections = getModifierMinSelections(modifier);

  if (!minSelections && !maxSelections) {
    return null;
  }

  if (maxSelections === minSelections) {
    return `Select ${maxSelections}`;
  }
  if (minSelections > 0) {
    return `Select at least ${minSelections}`;
  }
  if (maxSelections > 1) {
    return `Select up to ${maxSelections}`;
  }
  return null;
}

export function isMultiSelect(modifier) {
  const maxSelection = getModifierMaxSelections(modifier);
  if (maxSelection === 1) {
    return false;
  }
  return (
    modifier.modifier_type === modifierTypes.Multi_Selected ||
    isAutoApplied(modifier) ||
    getModifierMinSelections(modifier) > 1
  );
}

export function isAutoApplied(modifier) {
  return modifier.modifier_type === modifierTypes.Auto_Applied;
}

export function optionsHasChildren(option) {
  return option.modifiers_children?.length > 0;
}
